<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: Boolean,
  hour: { type: [String, Number], default: '' },
  minute: { type: [String, Number], default: '' },
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 5;
const CENTER_INDEX = Math.floor(VISIBLE_COUNT / 2);

const now = new Date();
const currentHour = now.getHours();
const currentMinute = now.getMinutes();

const selectedHour = ref(parseInt(props.hour) || currentHour);
const selectedMinute = ref(parseInt(props.minute) || currentMinute);

const overlayRef = ref(null);
const handleOverlayClick = (e) => {
  if (e.target === overlayRef.value) close();
};

watch(
  () => [props.hour, props.minute],
  ([h, m]) => {
    const parsedHour = parseInt(h);
    const parsedMin = parseInt(m);
    selectedHour.value = isNaN(parsedHour) ? currentHour : parsedHour;
    selectedMinute.value = isNaN(parsedMin) ? currentMinute : parsedMin;
  }
);

const hours = computed(() => {
  const arr = [];
  for (let i = 0; i <= 23; i++) arr.push(i);
  return arr;
});

const minutes = computed(() => {
  const arr = [];
  for (let i = 0; i <= 59; i++) arr.push(i);
  return arr;
});

const close = () => emit('update:modelValue', false);

const confirm = () => {
  emit('confirm', {
    hour: String(selectedHour.value).padStart(2, '0'),
    minute: String(selectedMinute.value).padStart(2, '0'),
  });
  close();
};

// ──────────── 터치 기반 휠 피커 로직 ────────────
const hourCol = ref(null);
const minuteCol = ref(null);

const hourOffset = ref(0);
const minuteOffset = ref(0);

const getInitialOffset = (items, selected) => {
  const idx = items.indexOf(selected);
  return idx >= 0 ? -idx * ITEM_HEIGHT + CENTER_INDEX * ITEM_HEIGHT : 0;
};

const getItemStyle = (offset, index) => {
  const y = offset + index * ITEM_HEIGHT;
  const centerY = CENTER_INDEX * ITEM_HEIGHT;
  const diff = y - centerY;
  const absDiff = Math.abs(diff);
  const maxDist = ITEM_HEIGHT * 2.5;

  const opacity = Math.max(0.15, 1 - absDiff / maxDist * 0.85);
  const scale = Math.max(0.88, 1 - absDiff / (maxDist * 3));

  return {
    transform: `translateY(${offset + index * ITEM_HEIGHT}px) scale(${scale})`,
    opacity,
    transition: 'none',
  };
};

const getItemClass = (offset, index) => {
  const y = offset + index * ITEM_HEIGHT;
  const centerY = CENTER_INDEX * ITEM_HEIGHT;
  return Math.abs(y - centerY) < ITEM_HEIGHT / 2 ? 'selected' : '';
};

const createDragHandler = (offsetRef, items, onSelect) => {
  let startY = 0;
  let startOffset = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;
  let animFrame = null;
  let isDragging = false;

  const snapToNearest = (currentOffset) => {
    const idx = Math.round(-(currentOffset - CENTER_INDEX * ITEM_HEIGHT) / ITEM_HEIGHT);
    const clampedIdx = Math.max(0, Math.min(items.value.length - 1, idx));
    const targetOffset = -clampedIdx * ITEM_HEIGHT + CENTER_INDEX * ITEM_HEIGHT;

    let current = currentOffset;
    const animate = () => {
      const diff = targetOffset - current;
      if (Math.abs(diff) < 0.5) {
        offsetRef.value = targetOffset;
        onSelect(items.value[clampedIdx]);
        return;
      }
      current += diff * 0.25;
      offsetRef.value = current;
      animFrame = requestAnimationFrame(animate);
    };
    animate();
  };

  const onStart = (e) => {
    if (animFrame) cancelAnimationFrame(animFrame);
    isDragging = true;
    const touch = e.touches ? e.touches[0] : e;
    startY = touch.clientY;
    startOffset = offsetRef.value;
    lastY = startY;
    lastTime = Date.now();
    velocity = 0;
  };

  const onMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    const currentY = touch.clientY;
    const now = Date.now();
    const dt = now - lastTime;

    if (dt > 0) {
      velocity = (currentY - lastY) / dt;
    }

    lastY = currentY;
    lastTime = now;
    offsetRef.value = startOffset + (currentY - startY);
  };

  const onEnd = () => {
    if (!isDragging) return;
    isDragging = false;

    let currentOffset = offsetRef.value;
    const deceleration = 0.95;
    let v = velocity * 15;

    if (Math.abs(v) > 2) {
      const inertia = () => {
        v *= deceleration;
        currentOffset += v;

        const maxOffset = CENTER_INDEX * ITEM_HEIGHT;
        const minOffset = -(items.value.length - 1) * ITEM_HEIGHT + CENTER_INDEX * ITEM_HEIGHT;

        if (currentOffset > maxOffset + ITEM_HEIGHT) {
          currentOffset = maxOffset + ITEM_HEIGHT;
          v = 0;
        }
        if (currentOffset < minOffset - ITEM_HEIGHT) {
          currentOffset = minOffset - ITEM_HEIGHT;
          v = 0;
        }

        offsetRef.value = currentOffset;

        if (Math.abs(v) > 0.5) {
          animFrame = requestAnimationFrame(inertia);
        } else {
          snapToNearest(currentOffset);
        }
      };
      animFrame = requestAnimationFrame(inertia);
    } else {
      snapToNearest(currentOffset);
    }
  };

  const cleanup = () => {
    if (animFrame) cancelAnimationFrame(animFrame);
  };

  return { onStart, onMove, onEnd, cleanup, snapToNearest };
};

const hourHandler = createDragHandler(hourOffset, hours, (v) => { selectedHour.value = v; });
const minuteHandler = createDragHandler(minuteOffset, minutes, (v) => { selectedMinute.value = v; });

const handleItemClick = (handler, offsetRef, items, index, onSelect) => {
  const targetOffset = -index * ITEM_HEIGHT + CENTER_INDEX * ITEM_HEIGHT;
  let current = offsetRef.value;
  const animate = () => {
    const diff = targetOffset - current;
    if (Math.abs(diff) < 0.5) {
      offsetRef.value = targetOffset;
      onSelect(items.value[index]);
      return;
    }
    current += diff * 0.2;
    offsetRef.value = current;
    requestAnimationFrame(animate);
  };
  animate();
};

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      nextTick(() => {
        hourOffset.value = getInitialOffset(hours.value, selectedHour.value);
        minuteOffset.value = getInitialOffset(minutes.value, selectedMinute.value);
      });
    }
  }
);

onBeforeUnmount(() => {
  hourHandler.cleanup();
  minuteHandler.cleanup();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="picker-fade">
      <div v-if="modelValue" ref="overlayRef" class="picker-overlay" @click="handleOverlayClick">
        <Transition name="picker-slide">
          <div v-if="modelValue" class="picker-sheet">
            <div class="picker-header">
              <button type="button" class="picker-header__cancel" @click="close">취소</button>
              <span class="picker-header__title">시간 선택</span>
              <button type="button" class="picker-header__confirm" @click="confirm">확인</button>
            </div>

            <div class="picker-body">
              <div class="picker-columns">
                <!-- 시 -->
                <div
                  ref="hourCol"
                  class="picker-column"
                  @touchstart.passive="hourHandler.onStart"
                  @touchmove="hourHandler.onMove"
                  @touchend="hourHandler.onEnd"
                  @mousedown="hourHandler.onStart"
                  @mousemove="hourHandler.onMove"
                  @mouseup="hourHandler.onEnd"
                  @mouseleave="hourHandler.onEnd"
                >
                  <div class="picker-column__items" :style="{ height: ITEM_HEIGHT * VISIBLE_COUNT + 'px' }">
                    <div
                      v-for="(hour, i) in hours"
                      :key="hour"
                      class="picker-column__item"
                      :class="getItemClass(hourOffset, i)"
                      :style="{ ...getItemStyle(hourOffset, i), height: ITEM_HEIGHT + 'px', lineHeight: ITEM_HEIGHT + 'px' }"
                      @click="handleItemClick(hourHandler, hourOffset, hours, i, (v) => { selectedHour = v })"
                    >
                      {{ String(hour).padStart(2, '0') }}<span class="unit">시</span>
                    </div>
                  </div>
                </div>

                <!-- 분 -->
                <div
                  ref="minuteCol"
                  class="picker-column"
                  @touchstart.passive="minuteHandler.onStart"
                  @touchmove="minuteHandler.onMove"
                  @touchend="minuteHandler.onEnd"
                  @mousedown="minuteHandler.onStart"
                  @mousemove="minuteHandler.onMove"
                  @mouseup="minuteHandler.onEnd"
                  @mouseleave="minuteHandler.onEnd"
                >
                  <div class="picker-column__items" :style="{ height: ITEM_HEIGHT * VISIBLE_COUNT + 'px' }">
                    <div
                      v-for="(minute, i) in minutes"
                      :key="minute"
                      class="picker-column__item"
                      :class="getItemClass(minuteOffset, i)"
                      :style="{ ...getItemStyle(minuteOffset, i), height: ITEM_HEIGHT + 'px', lineHeight: ITEM_HEIGHT + 'px' }"
                      @click="handleItemClick(minuteHandler, minuteOffset, minutes, i, (v) => { selectedMinute = v })"
                    >
                      {{ String(minute).padStart(2, '0') }}<span class="unit">분</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="picker-highlight"></div>
              <div class="picker-mask picker-mask--top"></div>
              <div class="picker-mask picker-mask--bottom"></div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.picker-sheet {
  width: 100%;
  max-width: 500px;
  background: #f7f7f7;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f7f7f7;
  border-bottom: 0.5px solid #c8c8c8;

  &__title {
    font-size: 13px;
    font-weight: 400;
    color: #8e8e93;
    letter-spacing: -0.1px;
  }

  &__cancel,
  &__confirm {
    font-size: 16px;
    background: none;
    border: none;
    padding: 4px 2px;
    cursor: pointer;
    font-weight: 500;
  }

  &__cancel {
    color: #007aff;
  }

  &__confirm {
    color: #007aff;
    font-weight: 600;
  }
}

.picker-body {
  position: relative;
  padding: 8px 0;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  background: #fff;
}

.picker-columns {
  display: flex;
  justify-content: center;
  max-width: 180px;
  margin: 0 auto;
  gap: 0;
}

.picker-column {
  flex: 0 0 80px;
  overflow: hidden;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &__items {
    position: relative;
    overflow: hidden;
  }

  &__item {
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #bbb;
    white-space: nowrap;
    will-change: transform, opacity;
    pointer-events: auto;
    font-variant-numeric: tabular-nums;

    .unit {
      font-size: inherit;
      margin-left: 1px;
      color: inherit;
      font-weight: inherit;
    }

    &.selected {
      color: #000;
      font-weight: 400;
      font-size: 22px;
    }
  }
}

.picker-highlight {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 40px;
  border-top: 0.5px solid #c8c8c8;
  border-bottom: 0.5px solid #c8c8c8;
  pointer-events: none;
  z-index: 1;
}

.picker-mask {
  position: absolute;
  left: 0;
  right: 0;
  height: 72px;
  pointer-events: none;
  z-index: 2;

  &--top {
    top: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
  }

  &--bottom {
    bottom: 0;
    background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
  }
}

.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.3s ease;
}
.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
}

.picker-slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.picker-slide-leave-active {
  transition: transform 0.25s ease-in;
}
.picker-slide-enter-from {
  transform: translateY(100%);
}
.picker-slide-leave-to {
  transform: translateY(100%);
}
</style>
